<script setup>
import { computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Badge from '@/components/Badge.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlusSmall from '@/components/icon/IconPlusSmall.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconCalendarOutline from '@/components/icon/IconCalendarOutline.vue'
import { scheduleTypeMeta, describeSchedule } from './mediaflowMeta'

const props = defineProps({
	schedules: { type: Array, default: () => [] },
	flows: { type: Array, default: () => [] },
	maxSchedules: { type: Number, default: 0 },
	togglingId: { type: String, default: null },
	/** Deleting is blocked while the production is online (as before). */
	deleteLocked: { type: Boolean, default: false },
	/** Production disabled: schedules can be read but not changed. */
	enabled: { type: Boolean, default: true },
	loading: { type: Boolean, default: false },
})

const emit = defineEmits(['add', 'toggle', 'edit', 'delete'])

const atLimit = computed(() => props.maxSchedules > 0 && props.schedules.length >= props.maxSchedules)
const activeCount = computed(() => props.schedules.filter((s) => s.enabled).length)
const flowName = (id) => props.flows.find((f) => String(f._id) === String(id))?.name || ''
const summary = (schedule) => describeSchedule(schedule, flowName)
</script>

<template>
	<section class="rounded-xl bg-surface-2 ring-1 ring-surface-3" aria-labelledby="mf-schedules-heading">
		<header class="flex flex-col gap-3 px-4 pt-4 pb-3 sm:flex-row sm:items-center sm:justify-between">
			<div class="min-w-0">
				<h2 id="mf-schedules-heading" class="m-0 flex items-center gap-2 text-base font-semibold text-surface-text-2">
					Schedules
					<span v-if="schedules.length" class="rounded-full bg-surface-3 px-2 py-0.5 text-xs font-medium text-surface-8">
						{{ activeCount }} active · {{ schedules.length }}<template v-if="maxSchedules > 0"> / {{ maxSchedules }}</template>
					</span>
				</h2>
				<p class="m-0 mt-0.5 text-xs text-surface-8">One-off switches, recurring breaks, rotations and ad blocks.</p>
			</div>
			<Tooltip v-if="atLimit" wrap-trigger content="Upgrade your subscription to add more schedules">
				<FormButton size="sm" type="main" :disabled="true">
					<icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14"><icon-plus-small /></icon-base>
					<span class="ml-1.5">New schedule</span>
				</FormButton>
			</Tooltip>
			<FormButton v-else size="sm" type="main" :disabled="!enabled" :title="!enabled ? 'Enable the production first' : undefined" data-testid="new-schedule" @click="emit('add')">
				<icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14"><icon-plus-small /></icon-base>
				<span class="ml-1.5">New schedule</span>
			</FormButton>
		</header>

		<div v-if="loading" class="flex flex-col gap-2 px-3 pb-3" aria-busy="true">
			<div v-for="n in 3" :key="n" class="h-[68px] animate-pulse rounded-lg bg-surface-1" />
		</div>

		<EmptyState v-else-if="!schedules.length" title="No schedules yet" subtitle="Switch to a scene or break at set times." min-height="0" class="pb-8 pt-4">
			<template #icon>
				<icon-base width="24" height="24" fill="none"><icon-calendar-outline /></icon-base>
			</template>
		</EmptyState>

		<ul v-else class="m-0 flex list-none flex-col gap-1.5 px-3 pb-3">
			<li
				v-for="schedule in schedules"
				:key="schedule._id"
				class="group flex flex-col gap-3 rounded-lg border border-transparent border-l-2 bg-surface-1 p-3 transition-colors hover:border-surface-4 sm:flex-row sm:items-center"
				:class="[schedule.enabled ? '!border-l-success' : '!border-l-surface-4', !enabled ? 'opacity-70' : '']"
				:data-schedule-enabled="schedule.enabled ? 'true' : 'false'"
			>
				<div class="flex min-w-0 flex-1 items-start gap-3">
					<span class="flex size-10 shrink-0 items-center justify-center rounded-lg" :class="schedule.enabled ? 'bg-success-transparent text-success' : 'bg-surface-3 text-surface-8'">
						<component :is="scheduleTypeMeta(schedule.type).icon" />
					</span>
					<div class="flex min-w-0 flex-1 flex-col gap-0.5">
						<span class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
							<span class="truncate text-sm font-medium text-surface-text-2" :title="schedule.name">{{ schedule.name }}</span>
							<Badge type="main" size="xs" class="uppercase tracking-wider">{{ scheduleTypeMeta(schedule.type).label }}</Badge>
							<StatusPill v-if="schedule.enabled" variant="success" size="sm">Active</StatusPill>
							<StatusPill v-else variant="muted" size="sm">Paused</StatusPill>
						</span>
						<span class="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-surface-8">
							<template v-for="(part, i) in summary(schedule)" :key="i">
								<span v-if="i > 0" aria-hidden="true">·</span>
								<span class="truncate" :class="i === summary(schedule).length - 1 ? 'text-surface-8' : ''">{{ part }}</span>
							</template>
						</span>
					</div>
				</div>

				<div class="flex items-center justify-between gap-2 pl-[52px] sm:justify-end sm:pl-0">
					<Tooltip :content="schedule.enabled ? 'Pause this schedule' : 'Activate this schedule'" wrapper-class="inline-flex">
						<FormSwitch
							size="md"
							:model-value="!!schedule.enabled"
							:disabled="!enabled || togglingId === schedule._id"
							:aria-label="schedule.enabled ? `Pause ${schedule.name}` : `Activate ${schedule.name}`"
							@update:model-value="emit('toggle', schedule)"
						/>
					</Tooltip>
					<div class="flex items-center gap-1">
						<FormButton is-icon size="sm" type="secondary" :disabled="!enabled" :aria-label="`Edit ${schedule.name}`" title="Edit" @click="emit('edit', schedule)">
							<icon-base><icon-edit /></icon-base>
						</FormButton>
						<Tooltip :content="deleteLocked ? 'Stop the production to delete schedules' : 'Delete'" wrapper-class="inline-flex" wrap-trigger>
							<FormButton is-icon size="sm" type="danger" :disabled="deleteLocked || !enabled" :aria-label="`Delete ${schedule.name}`" @click="emit('delete', schedule)">
								<icon-base><icon-bin /></icon-base>
							</FormButton>
						</Tooltip>
					</div>
				</div>
			</li>
		</ul>
	</section>
</template>
